import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { Dispatch, ReactNode, SetStateAction } from "react";

import { registerNotifier } from "services/notifier";

import type { NotifyObj, NotifyType } from "types";

// Split in two on purpose: everything that only raises a toast subscribes to the
// actions, whose identity never changes, so showing a notification re-renders the
// snackbar alone instead of every page that can report an error.
type NotifyActions = {
  notify: (type: NotifyType, text?: string) => void;
  notifySuccess: (text?: string) => void;
  notifyError: (text?: string) => void;
  hideNotif: () => void;
  // Controllers take the raw setter: `new XController(setNotifObj)`.
  setNotifObj: Dispatch<SetStateAction<NotifyObj>>;
};

const NotifyActionsContext = createContext<NotifyActions | null>(null);
const NotifyStateContext = createContext<NotifyObj | null>(null);

NotifyActionsContext.displayName = "NotifyActionsContext";
NotifyStateContext.displayName = "NotifyStateContext";

const HIDDEN: NotifyObj = Object.freeze({ show: false, type: "", text: "" });

// An error carries the status, the path and the server message, so it stays up long
// enough to be read and copied.
const VISIBLE_MS: Record<string, number> = { error: 10000, default: 2000 };

function NotifyProvider({ children }: { children: ReactNode }) {
  const [notifObj, setNotifObj] = useState<NotifyObj>(HIDDEN);

  const notify = useCallback((type: NotifyType, text?: string) => {
    setNotifObj({ show: true, type: type, text: text });
  }, []);

  const notifySuccess = useCallback((text?: string) => notify("success", text), [notify]);
  const notifyError = useCallback((text?: string) => notify("error", text), [notify]);

  const hideNotif = useCallback(() => {
    setNotifObj((prev) => (prev.show ? { ...prev, show: false } : prev));
  }, []);

  useEffect(() => {
    if (!notifObj.show) return undefined;

    const id = setTimeout(hideNotif, VISIBLE_MS[notifObj.type] || VISIBLE_MS.default);

    return () => clearTimeout(id);
  }, [notifObj, hideNotif]);

  // The api modules that predate the notifier (dashboard, tickets, languages,
  // conversion, reports generator, crypto address generator) build FetchRequest with
  // no setter of their own, so their failures had nowhere to go. Give them one.
  useEffect(() => {
    registerNotifier(setNotifObj);

    return () => registerNotifier(null);
  }, []);

  const actions = useMemo(
    () => ({
      notify: notify,
      notifySuccess: notifySuccess,
      notifyError: notifyError,
      hideNotif: hideNotif,
      setNotifObj: setNotifObj,
    }),
    [notify, notifySuccess, notifyError, hideNotif]
  );

  return (
    <NotifyActionsContext.Provider value={actions}>
      <NotifyStateContext.Provider value={notifObj}>{children}</NotifyStateContext.Provider>
    </NotifyActionsContext.Provider>
  );
}

function useNotify(): NotifyActions {
  const context = useContext(NotifyActionsContext);

  if (!context) {
    throw new Error("useNotify must be used inside AppProvider.");
  }

  return context;
}

// Only the snackbar and the two pages that react to a failed request need this one.
function useNotifState(): NotifyObj {
  const context = useContext(NotifyStateContext);

  if (!context) {
    throw new Error("useNotifState must be used inside AppProvider.");
  }

  return context;
}

export { NotifyProvider, useNotify, useNotifState };
